import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconPencil,
  IconPlus,
  IconRefresh,
  IconStar,
  IconToggleLeft,
  IconToggleRight,
  IconTrash,
} from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import type { ApiData } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { graphql } from '@/lib/graphql';

const AiModelsSubscription = graphql(`
  subscription AiModels {
    aiModels(orderBy: { name: ASC }) {
      id
      name
      description
      modelId
      isDefault
      isActive
      personasAggregate(where: { deletedAt: { _isNull: true } }) {
        aggregate {
          count
        }
      }
    }
  }
`);

const InsertAiModelMutation = graphql(`
  mutation InsertAiModel($name: String!, $description: String, $modelId: String!, $isDefault: Boolean!) {
    insertAiModelsOne(
      object: { name: $name, description: $description, modelId: $modelId, isDefault: $isDefault, isActive: true }
    ) {
      id
    }
  }
`);

const UpdateAiModelMutation = graphql(`
  mutation UpdateAiModel($id: Uuid!, $name: String!, $description: String, $modelId: String!) {
    updateAiModelsByPk(pkColumns: { id: $id }, _set: { name: $name, description: $description, modelId: $modelId }) {
      id
    }
  }
`);

const SetAiModelActiveMutation = graphql(`
  mutation SetAiModelActive($id: Uuid!, $isActive: Boolean!) {
    updateAiModelsByPk(pkColumns: { id: $id }, _set: { isActive: $isActive }) {
      id
      isActive
    }
  }
`);

const SetDefaultAiModelMutation = graphql(`
  mutation SetDefaultAiModel($id: Uuid!) {
    clearDefault: updateAiModels(where: { isDefault: { _eq: true } }, _set: { isDefault: false }) {
      affectedRows
    }
    setDefault: updateAiModelsByPk(pkColumns: { id: $id }, _set: { isDefault: true }) {
      id
      isDefault
    }
  }
`);

const DeleteAiModelMutation = graphql(`
  mutation DeleteAiModel($id: Uuid!) {
    deleteAiModelsByPk(id: $id) {
      id
    }
  }
`);

type AvailableModel = ApiData<(typeof api)['ai-models']['refresh']['post']>['models'][number];

type AiModel = {
  id: string;
  name: string;
  description: string | null;
  modelId: string;
  isDefault: boolean;
  isActive: boolean;
  personaCount: number;
};

type ModelForm = { id: string | null; name: string; description: string; modelId: string | null };

const emptyForm: ModelForm = { id: null, name: '', description: '', modelId: null };

function pricePerMillion(price: string) {
  return (parseFloat(price) * 1_000_000).toFixed(2);
}

function availableModelLabel(model: AvailableModel) {
  return `${model.name} — $${pricePerMillion(model.pricing.prompt)}/$${pricePerMillion(model.pricing.completion)} per 1M tokens`;
}

type AvailableModelsState = { isLoading: boolean; models: AvailableModel[]; error: string | null };

async function fetchAvailableModels() {
  const result = await api['ai-models'].refresh.post();

  if (result.error) {
    return { isLoading: false, models: [], error: getErrorMessage(result.error, 'Failed to load models') };
  }

  return { isLoading: false, models: result.data.models, error: null };
}

export const Route = createFileRoute('/_authed/system/ai-models')({
  component: SystemAiModelsPage,
});

function StatusBadge({ isActive }: { isActive: boolean }) {
  if (isActive) {
    return (
      <Badge color="green" variant="light">
        Active
      </Badge>
    );
  }

  return (
    <Badge color="gray" variant="light">
      Inactive
    </Badge>
  );
}

function ModelRow({
  model,
  onEdit,
  onSetDefault,
  onToggleActive,
  onDelete,
}: {
  model: AiModel;
  onEdit: (model: AiModel) => void;
  onSetDefault: (model: AiModel) => void;
  onToggleActive: (model: AiModel) => void;
  onDelete: (model: AiModel) => void;
}) {
  return (
    <Table.Tr opacity={model.isActive ? 1 : 0.5}>
      <Table.Td>
        <Group gap="xs">
          {model.name}
          {model.isDefault && (
            <Badge size="xs" color="yellow" leftSection={<IconStar size={10} />}>
              Default
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td maw={250}>
        <Text size="sm" c="dimmed" lineClamp={1}>
          {model.description || '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed" ff="monospace">
          {model.modelId}
        </Text>
      </Table.Td>
      <Table.Td>
        <StatusBadge isActive={model.isActive} />
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Tooltip label="Edit">
            <ActionIcon variant="subtle" onClick={() => onEdit(model)}>
              <IconPencil size={16} />
            </ActionIcon>
          </Tooltip>
          {!model.isDefault && model.isActive && (
            <Tooltip label="Set as Default">
              <ActionIcon variant="subtle" color="yellow" onClick={() => onSetDefault(model)}>
                <IconStar size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label={model.isActive ? 'Deactivate' : 'Activate'}>
            <ActionIcon
              variant="subtle"
              color={model.isActive ? 'orange' : 'green'}
              onClick={() => onToggleActive(model)}
              disabled={model.isDefault && model.isActive}
            >
              {model.isActive ? <IconToggleRight size={16} /> : <IconToggleLeft size={16} />}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete">
            <ActionIcon variant="subtle" color="red" onClick={() => onDelete(model)} disabled={model.isDefault}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

function SystemAiModelsPage() {
  const [{ data }] = useSubscription({ query: AiModelsSubscription });
  const [, insertModel] = useMutation(InsertAiModelMutation);
  const [, updateModel] = useMutation(UpdateAiModelMutation);
  const [, setModelActive] = useMutation(SetAiModelActiveMutation);
  const [, setDefaultModel] = useMutation(SetDefaultAiModelMutation);
  const [, deleteModel] = useMutation(DeleteAiModelMutation);

  const [available, setAvailable] = useState<AvailableModelsState>({ isLoading: true, models: [], error: null });
  const [listError, setListError] = useState<string | null>(null);

  const [form, setForm] = useState<ModelForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [deleting, setDeleting] = useState<AiModel | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const refreshAvailableModels = useCallback(async () => {
    setAvailable((current) => ({ ...current, isLoading: true, error: null }));
    setAvailable(await fetchAvailableModels());
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchAvailableModels().then((next) => {
      if (!cancelled) setAvailable(next);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const models = useMemo<AiModel[]>(
    () =>
      (data?.aiModels ?? []).map((m) => ({
        id: m.id,
        name: m.name,
        description: m.description,
        modelId: m.modelId,
        isDefault: m.isDefault,
        isActive: m.isActive,
        personaCount: m.personasAggregate.aggregate?.count ?? 0,
      })),
    [data],
  );

  const modelSelectData = useMemo(() => {
    const options = available.models.map((m) => ({ value: m.id, label: availableModelLabel(m) }));
    const selected = form?.modelId;

    if (selected && !options.some((o) => o.value === selected)) options.unshift({ value: selected, label: selected });

    return options;
  }, [available.models, form?.modelId]);

  const openCreateModal = () => {
    setForm(emptyForm);
    setFormError(null);
  };

  const openEditModal = (model: AiModel) => {
    setForm({ id: model.id, name: model.name, description: model.description ?? '', modelId: model.modelId });
    setFormError(null);
  };

  const openDeleteModal = (model: AiModel) => {
    setDeleting(model);
    setDeleteError(null);
  };

  const submitForm = async () => {
    if (!form) return;

    const name = form.name.trim();
    const description = form.description.trim() || null;

    if (!name || !form.modelId) return;

    if (models.some((m) => m.id !== form.id && m.name === name)) {
      return setFormError(`A model with the name "${name}" already exists`);
    }

    setIsSubmitting(true);
    setFormError(null);

    const result = form.id
      ? await updateModel({ id: form.id, name, description, modelId: form.modelId })
      : await insertModel({
          name,
          description,
          modelId: form.modelId,
          isDefault: !models.some((m) => m.isActive),
        });

    setIsSubmitting(false);

    if (result.error) return setFormError(getErrorMessage(result.error, 'Operation failed'));

    setForm(null);
  };

  const confirmDelete = async () => {
    if (!deleting) return;

    if (deleting.isDefault) {
      return setDeleteError('Cannot delete the default model. Set another model as default first.');
    }

    if (deleting.personaCount > 0) {
      return setDeleteError(
        'Cannot delete this model — it is referenced by one or more personas. Deactivate it instead.',
      );
    }

    setIsSubmitting(true);
    setDeleteError(null);

    const result = await deleteModel({ id: deleting.id });

    setIsSubmitting(false);

    if (result.error) return setDeleteError(getErrorMessage(result.error, 'Delete failed'));

    setDeleting(null);
  };

  const toggleActive = async (model: AiModel) => {
    if (model.isActive && model.isDefault) {
      return setListError('Cannot deactivate the default model. Set another model as default first.');
    }

    setListError(null);

    const result = await setModelActive({ id: model.id, isActive: !model.isActive });

    if (result.error) setListError(getErrorMessage(result.error, 'Failed to change model status'));
  };

  const makeDefault = async (model: AiModel) => {
    if (!model.isActive) return setListError('Cannot set an inactive model as default');

    setListError(null);

    const result = await setDefaultModel({ id: model.id });

    if (result.error) setListError(getErrorMessage(result.error, 'Failed to set the default model'));
  };

  if (data === undefined) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <>
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Group justify="space-between">
            <div>
              <Title order={1}>AI Models</Title>
              <Text c="dimmed" mt="xs">
                Manage AI model aliases available to organizations
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal} loading={available.isLoading}>
              Add Model
            </Button>
          </Group>

          <ErrorAlert message={available.error} />
          <ErrorAlert message={listError} />

          <Paper withBorder radius="lg">
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Underlying Model</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th w={160}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {models.map((model) => (
                  <ModelRow
                    key={model.id}
                    model={model}
                    onEdit={openEditModal}
                    onSetDefault={(m) => void makeDefault(m)}
                    onToggleActive={(m) => void toggleActive(m)}
                    onDelete={openDeleteModal}
                  />
                ))}
                {models.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" ta="center" py="md">
                        No models configured yet. Add your first model to get started.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      </Container>

      <Modal
        opened={form !== null}
        onClose={() => setForm(null)}
        title={form?.id ? 'Edit Model' : 'Add Model'}
        size="lg"
      >
        {form && (
          <Stack>
            <ErrorAlert message={formError} />
            <TextInput
              label="Name"
              description="The alias organizations will see"
              placeholder="e.g., Fast Assistant"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.currentTarget.value })}
              required
            />
            <Textarea
              label="Description"
              description="Optional context for organization users"
              placeholder="e.g., Best for quick, simple conversations"
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.currentTarget.value })}
            />
            <Group align="flex-end" gap="xs">
              <Select
                label="Underlying Model"
                description="The OpenRouter model powering this alias"
                placeholder={available.isLoading ? 'Loading models...' : 'Select a model'}
                data={modelSelectData}
                value={form.modelId}
                onChange={(value) => setForm({ ...form, modelId: value })}
                searchable
                required
                nothingFoundMessage={available.isLoading ? 'Loading...' : 'No models found'}
                style={{ flex: 1 }}
              />
              <Tooltip label="Refresh models from OpenRouter">
                <ActionIcon
                  variant="subtle"
                  onClick={() => void refreshAvailableModels()}
                  loading={available.isLoading}
                  mb={2}
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setForm(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => void submitForm()}
                loading={isSubmitting}
                disabled={!form.name.trim() || !form.modelId}
              >
                {form.id ? 'Save' : 'Add'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal opened={deleting !== null} onClose={() => setDeleting(null)} title="Delete Model">
        <Stack>
          <ErrorAlert message={deleteError} />
          <Text>
            Are you sure you want to delete this model? This cannot be undone. If personas reference this model, you
            must deactivate it instead.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={() => void confirmDelete()} loading={isSubmitting}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
